// src/pages/Dashboard/InfographicPage.jsx
// หน้า Infographic สรุปสถิติหนึ่งหน้า (A4) พิมพ์จาก /dashboard
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spin, message } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH');

// CSS สำหรับพิมพ์ A4 แบบ one page (ซ่อน toolbar, กำหนดขนาดหน้ากระดาษให้พอดีเป๊ะ)
const PRINT_STYLES = `
  @page { size: A4 portrait; margin: 0; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .no-print { display: none !important; }
    .infographic-shell {
      padding: 0 !important;
      margin: 0 !important;
      min-height: 0 !important;
      background: #fff !important;
    }
    .infographic-page {
      box-sizing: border-box !important;
      width: 210mm !important;
      min-height: 297mm !important;
      margin: 0 !important;
      padding: 8mm !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      font-family: 'Prompt', 'Kanit', sans-serif !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .infographic-page, .infographic-page * { box-sizing: border-box !important; }
  }
`;

// ขอบเขตจากโปรไฟล์ผู้ใช้ (ภาค = ทั้งหมด, จังหวัด/อำเภอ/ตำบล = ของตัวเอง)
function buildScope(profile) {
  const p = {};
  if (!profile) return p;
  if (profile.level === 'ตำบล' && profile.subdistrict) {
    p.province = profile.province;
    p.district = profile.district;
    p.subdistrict = profile.subdistrict;
  } else if (profile.level === 'อำเภอ' && profile.district) {
    p.province = profile.province;
    p.district = profile.district;
  } else if (profile.level === 'จังหวัด' && profile.province) {
    p.province = profile.province;
  }
  return p;
}

const InfographicPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const profileRes = await axios.get(`${API_BASE_URL}/api/users/me`, { headers });
        const p = { ...profileRes.data };
        setProfile(p);
        const sumRes = await axios.get(`${API_BASE_URL}/api/temple-statistics/summary`, {
          headers,
          params: buildScope(p),
        });
        setData(sumRes.data);
      } catch (err) {
        message.error('ไม่สามารถโหลดข้อมูลสำหรับ Infographic ได้');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ข้อมูลปีล่าสุดสำหรับตัวเลขหลัก
  const activeYear = useMemo(() => {
    if (!data || !data.byYear || !data.byYear.length) return null;
    return data.byYear.find((y) => y.year === data.latestYear) || data.byYear[data.byYear.length - 1];
  }, [data]);

  const totalTemples = useMemo(
    () => (data?.templeCountByProvince || []).reduce((s, p) => s + (p.total || 0), 0),
    [data]
  );

  const scopeLabel = useMemo(() => {
    if (!profile) return '';
    if (profile.level === 'ตำบล') return `ตำบล ${profile.subdistrict}`;
    if (profile.level === 'อำเภอ') return `อำเภอ ${profile.district}`;
    if (profile.level === 'จังหวัด') return `จังหวัด ${profile.province}`;
    return 'ทั่วทั้งภาค ๔-๕ (ธ)';
  }, [profile]);

  const heroCards = useMemo(() => {
    const y = activeYear || {};
    return [
      { label: 'วัดทั้งหมด', value: totalTemples, color: '#ad7c00' },
      { label: 'วัดที่ส่งข้อมูล', value: y.reporting_temples, color: '#1890ff' },
      { label: 'พระภิกษุ', value: y.monks_count, color: '#c0392b' },
      { label: 'สามเณร', value: y.novices_count, color: '#27ae60' },
      { label: 'ศิษย์วัด', value: y.temple_students_count, color: '#e67e22' },
      { label: 'ภิกษุ-สามเณรเปรียญ', value: y.pariyatti_count, color: '#8e44ad' },
      { label: 'นักธรรมเอก', value: (y.monks_naktham3_count || 0) + (y.novices_naktham3_count || 0), color: '#16a085' },
      { label: 'นักเรียนปริยัติ', value: y.priyatti_students_count, color: '#2c3e50' },
    ];
  }, [activeYear, totalTemples]);

  // กราฟแนวโน้มรายปี
  const trendData = useMemo(
    () => (data?.byYear || []).map((r) => ({
      label: `พ.ศ. ${r.year + 543}`,
      ภิกษุ: r.monks_count || 0,
      สามเณร: r.novices_count || 0,
      ศิษย์วัด: r.temple_students_count || 0,
    })),
    [data]
  );

  // กราฟแยกจังหวัด (ปีล่าสุด) - กรองกลุ่ม "(ไม่ระบุจังหวัด)" ออก
  const provinceBarData = useMemo(() => {
    if (!data || !data.byProvinceByYear) return [];
    return data.byProvinceByYear
      .filter((r) => r.year === data.latestYear && r.province && r.province !== '(ไม่ระบุจังหวัด)')
      .map((r) => ({ จังหวัด: r.province, ภิกษุ: r.monks_count || 0, สามเณร: r.novices_count || 0 }))
      .sort((a, b) => (b.ภิกษุ + b.สามเณร) - (a.ภิกษุ + a.สามเณร));
  }, [data]);

  // ตารางสรุปรายปี (HTML table เพื่อการพิมพ์ที่คมชัด)
  const yearRows = useMemo(() => (data?.byYear || []).map((r) => ({ ...r })), [data]);

  const printedDate = new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' });
  const yearRangeLabel = data && data.years && data.years.length
    ? `ปี พ.ศ. ${Math.min(...data.years) + 543} – ${Math.max(...data.years) + 543}`
    : '';

  // ยอดรวมของตารางสรุปรายปี
  const yearTotal = useMemo(() => {
    const keys = ['reporting_temples', 'monks_count', 'novices_count', 'temple_students_count',
      'pariyatti_count', 'monks_naktham3_count', 'novices_naktham3_count',
      'monks_patimokkha_count', 'monks_over10_pansa_count', 'monks_with_titles_count',
      'priyatti_students_count'];
    const t = Object.fromEntries(keys.map((k) => [k, 0]));
    for (const r of yearRows) for (const k of keys) t[k] += r[k] || 0;
    return t;
  }, [yearRows]);

  const pageStyle = {
    boxSizing: 'border-box',
    width: '794px',
    maxWidth: '100%',
    minHeight: '1123px',
    margin: '0 auto',
    background: '#fff',
    padding: '26px 30px',
    borderRadius: 12,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  };

  return (
    <div className="infographic-shell" style={{ background: '#f0f2f5', minHeight: '100vh', padding: 24, fontFamily: 'Prompt, Kanit, sans-serif' }}>
      <style>{PRINT_STYLES}</style>

      <div className="no-print" style={{ maxWidth: 794, margin: '0 auto 16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>
          กลับแดชบอร์ด
        </Button>
        <Button type="primary" size="large" icon={<PrinterOutlined />} onClick={() => window.print()}>
          พิมพ์ Infographic
        </Button>
      </div>

      <Spin spinning={loading}>
        <div className="infographic-page" style={pageStyle}>
          {/* ส่วนหัว */}
          <div style={{ background: 'linear-gradient(135deg, #ad7c00 0%, #d4a017 100%)', borderRadius: 12, padding: '20px 24px', color: '#fff' }}>
            <div style={{ fontSize: 24, fontWeight: 700 }}>ระบบสารสนเทศคณะสงฆ์ ภาค ๔-๕ (ธรรมยุต)</div>
            <div style={{ fontSize: 15, opacity: 0.95, marginTop: 2 }}>สรุปสถิติประจำปีจากแบบฟอร์มสถิติวัด (บัญชีกลางปี)</div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
              {yearRangeLabel} • ขอบเขต: {scopeLabel} • ข้อมูลล่าสุด พ.ศ. {activeYear ? activeYear.year + 543 : '-'}
            </div>
          </div>

          {/* ตัวเลขหลัก */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '18px 0' }}>
            {heroCards.map((c) => (
              <div
                key={c.label}
                style={{
                  flex: '1 1 22%', minWidth: 155, background: '#fff', borderRadius: 10,
                  borderTop: `4px solid ${c.color}`, padding: '10px 12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 30, fontWeight: 700, color: c.color }}>{fmt(c.value)}</div>
                <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{c.label}</div>
              </div>
            ))}
          </div>

          {/* กราฟ 2 อัน */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>📈 แนวโน้มรายปี</div>
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="ภิกษุ" stroke="#ad7c00" strokeWidth={2} />
                  <Line type="monotone" dataKey="สามเณร" stroke="#27ae60" strokeWidth={2} />
                  <Line type="monotone" dataKey="ศิษย์วัด" stroke="#1890ff" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>📊 พระภิกษุ/สามเณรแยกจังหวัด (ปีล่าสุด)</div>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={provinceBarData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="จังหวัด" angle={-45} textAnchor="end" interval={0} fontSize={11} height={60} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="ภิกษุ" fill="#ad7c00" />
                  <Bar dataKey="สามเณร" fill="#27ae60" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ตารางสรุปรายปี */}
          <div style={{ background: '#fff', borderRadius: 10, padding: 12, marginTop: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>📋 ตารางสรุปสถิติรายปี</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  {['ปี', 'วัดที่ส่งข้อมูล', 'ภิกษุ', 'สามเณร', 'ศิษย์วัด', 'เปรียญ', 'นักธรรมเอก', 'ปาฏิโมกข์', 'พรรษา 10+', 'สมณศักดิ์', 'นักเรียนปริยัติ'].map((h) => (
                    <th key={h} style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {yearRows.map((r) => (
                  <tr key={r.year}>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', fontWeight: 600 }}>พ.ศ. {r.year + 543}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.reporting_temples)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.monks_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.novices_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.temple_students_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.pariyatti_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt((r.monks_naktham3_count || 0) + (r.novices_naktham3_count || 0))}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.monks_patimokkha_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.monks_over10_pansa_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.monks_with_titles_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(r.priyatti_students_count)}</td>
                  </tr>
                ))}
                {yearRows.length > 0 && (
                  <tr style={{ background: '#fffbe6', fontWeight: 700 }}>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px' }}>รวม</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.reporting_temples)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.monks_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.novices_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.temple_students_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.pariyatti_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.monks_naktham3_count + yearTotal.novices_naktham3_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.monks_patimokkha_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.monks_over10_pansa_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.monks_with_titles_count)}</td>
                    <td style={{ border: '1px solid #e8e8e8', padding: '6px 8px', textAlign: 'right' }}>{fmt(yearTotal.priyatti_students_count)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ส่วนท้าย */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 12, color: '#777', flexWrap: 'wrap', gap: 8 }}>
            <div>ข้อมูลจากระบบสารสนเทศคณะสงฆ์ ภาค ๔-๕ (ธรรมยุต) • ตาราง templeStatistics</div>
            <div>ขอบเขต: {scopeLabel} • พิมพ์เมื่อ {printedDate}</div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default InfographicPage;

